/**
 * LotowheelModalApariencia — extensiones del modal de sorteo lotowheel.
 *
 * Extiende `LotowheelModule` (lotowheel.js) con métodos para los tabs
 * visuales del modal. Este archivo NO declara módulo nuevo: hace
 * `Object.assign(LotowheelModule, {...})` para agregar métodos.
 *
 * Mapa de secciones (orden del archivo):
 *   1. TAB APARIENCIA          tema visual + pack de sonido.
 *   2. TAB GENERAL             moneda (preset + override custom).
 *   3. TAB RUEDA               tipo de ruleta + apuestas disponibles.
 *   4. BRANDING                upload/reset de logos por sorteo.
 *   5. PRESENTACIÓN DEL CERO   modo + labels custom + preview canvas.
 */

import {
    LotowheelModule,
    THEMES,
    TIPOS_DE_RUEDA_PARA_UI_ADMIN,
    BET_MULTIPLIERS,
    getBetAvailability,
    TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN,
} from './lotowheel.js';
import { ROULETTE_TYPES as TIPOS_DE_RUEDA_SSOT } from '@shared/config/games/lotowheel/roulette-numbers-order-config.js';
import { App, Api } from '../../../admin.js';
import { MonedaService } from '../../../services/moneda-service.js';
import { t } from '../../../services/i18n-admin.js';
import { drawSectorPreview, COLOR_VERDE_LOTO_CANONICO } from '@shared/components/sector-preview.js';

// Los assets de branding viven en el backend público (api-loto37.*),
// no en el dominio del admin (admin-loto37.*). Sin prefijo, el <img> pide
// el path al dominio admin → Pages devuelve index.html (HTML) y el preview queda invisible.
const PUBLIC_API_ORIGIN = import.meta.env.VITE_API_URL_PUBLIC || '';
function absolutizarAssetUrl(url) {
    if (!url) return url;
    if (/^https?:\/\//.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
    return PUBLIC_API_ORIGIN + url;
}

Object.assign(LotowheelModule, {
    // ═══════════════════════════════════════════════════════════════════════
    // TAB APARIENCIA — tema visual + pack de sonido (theme grid clickable).
    // ═══════════════════════════════════════════════════════════════════════

    renderThemeGrid() {
        const grid = document.getElementById('themeGrid');
        if (!grid) return;
        grid.innerHTML = THEMES.map(
            t => `
      <div class="theme-option" data-theme="${t.id}" data-action="lotowheel:selectTheme">
        <div class="theme-preview" style="background: linear-gradient(135deg, ${t.colors[0]}, ${t.colors[1]}, ${t.colors[2]})"></div>
        <div class="theme-name">${t.name}</div>
      </div>
    `
        ).join('');
    },

    selectTheme(id) {
        document.querySelectorAll('.theme-option').forEach(o => {
            o.classList.toggle('selected', o.dataset.theme === id);
        });
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TAB GENERAL — moneda (preset MonedaService + override custom).
    // ═══════════════════════════════════════════════════════════════════════

    onMonedaChange() {
        const code = document.getElementById('lotowheelConfigMonedaCodigo').value;
        const preset = MonedaService.getPreset(code);
        if (preset) {
            document.getElementById('lotowheelConfigMonedaSimbolo').value = preset.simbolo;
            document.getElementById('lotowheelConfigMonedaDecimales').value = preset.decimales;
            document.getElementById('lotowheelConfigMonedaSepDec').value = preset.separadorDecimal;
            document.getElementById('lotowheelConfigMonedaSepMil').value = preset.separadorMiles;
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TAB RUEDA — tipo de ruleta + apuestas disponibles derivadas del SSOT.
    // ═══════════════════════════════════════════════════════════════════════

    onTipoRuletaChange() {
        const tipoId = document.getElementById('lotowheelConfigTipoRuleta').value;
        const tipo = TIPOS_DE_RUEDA_PARA_UI_ADMIN[tipoId];
        if (!tipo) return;

        // Derivar disponibilidad matematicamente (misma logica que SSOT)
        const avail = getBetAvailability(tipoId);

        // Actualizar panel informativo del tipo de rueda (blanco y verde)
        const infoPanel = document.getElementById('tipoRuletaInfo');
        if (infoPanel) {
            const mults = BET_MULTIPLIERS.forType(tipoId);
            const maxNum = tipo.sectors - 1;

            // Chips de multiplicadores para apuestas disponibles
            const betsInfo = [];
            betsInfo.push({ name: t('Pleno'), mult: mults.pleno, available: true });
            betsInfo.push({ name: t('Duo'), mult: mults.duo, available: true });
            betsInfo.push({ name: t('Trio'), mult: mults.trio, available: avail.hasTrio });
            betsInfo.push({ name: t('Cuarteto'), mult: mults.cuarteto, available: avail.hasCuarteto });
            betsInfo.push({ name: t('Sexteto'), mult: mults.sexteto, available: avail.hasSexteto });
            betsInfo.push({ name: t('Bloque'), mult: tipo.blockMult, available: true });

            let chipsHtml = '';
            for (let i = 0; i < betsInfo.length; i++) {
                const b = betsInfo[i];
                if (!b.available) continue;
                chipsHtml +=
                    '<span style="display:inline-block; padding:2px 8px; margin:2px; background:var(--bg-secondary); border-radius:4px; font-size:0.8rem;">' +
                    '<span style="color:var(--text-muted);">' +
                    b.name +
                    '</span>' +
                    '<span style="color:var(--accent-green,#4caf50); font-weight:600; margin-left:4px;">x' +
                    b.mult +
                    '</span>' +
                    '</span>';
            }

            // Especiales (espejos/gemelos — solo european 36 numeros)
            let specialHtml = '';
            if (avail.hasMirrors || avail.hasTwins) {
                const specials = [];
                if (avail.hasMirrors) specials.push(t('Espejos x') + mults.espejo);
                if (avail.hasTwins) specials.push(t('Gemelos x') + mults.gemelos);
                specialHtml =
                    '<div style="margin-top:6px; font-size:0.75rem; color:var(--text-muted);">' + t('Especiales:') + ' ' +
                    specials.join(', ') +
                    '</div>';
            }

            // Apuestas no disponibles en este tipo de rueda
            let restrictHtml = '';
            const restrictions = [];
            if (!avail.hasTrio) restrictions.push(t('Trio'));
            if (!avail.hasCuarteto) restrictions.push(t('Cuarteto'));
            if (!avail.hasSexteto) restrictions.push(t('Sexteto'));
            if (restrictions.length > 0) {
                restrictHtml =
                    '<div style="margin-top:6px; font-size:0.75rem; color:var(--text-muted);">' + t('No disponible:') + ' ' +
                    restrictions.join(', ') +
                    '</div>';
            }

            // Frecuencia de premios por tipo de apuesta (sin ponderación — referencia base)
            // "cada N tiradas" = sectors / numerosQueCubre
            const freqPleno = tipo.sectors; // 1 número → cada 37, 13, 19...
            const freqMitad = Math.round(tipo.sectors / (maxNum / 2)); // ~2 tiradas
            const freqBloque = Math.round(tipo.sectors / (maxNum / tipo.blockCount)); // ~6 tiradas

            // Volatilidad geométrica base (sin ponderación):
            // Alta = pocos números (premio raro, gran espera) → sectors <= 14
            // Media = ruedas intermedias → 15-24
            // Baja = ruedas grandes, premios frecuentes → 25+
            let volLabel, volColor;
            if (tipo.sectors <= 14) {
                volLabel = t('Alta (premios poco frecuentes)');
                volColor = '#ff9800';
            } else if (tipo.sectors <= 24) {
                volLabel = t('Media');
                volColor = '#4fc3f7';
            } else {
                volLabel = t('Baja (premios frecuentes)');
                volColor = '#4caf50';
            }

            infoPanel.innerHTML =
                // Fila 1: config + pleno destacado
                '<div style="display:flex; justify-content:space-between; align-items:flex-start; gap:16px;">' +
                '<div style="flex:1;">' +
                '<div style="font-size:0.7rem; text-transform:uppercase; color:var(--text-muted); letter-spacing:0.5px; margin-bottom:4px;">' + t('Configuracion') + '</div>' +
                '<div style="font-size:0.85rem; color:var(--text-secondary);">' +
                maxNum +
                ' nums &middot; ' +
                tipo.cols +
                ' ' + t('columnas') + ' &middot; ' +
                tipo.blockCount +
                ' ' + t('bloques') +
                '</div>' +
                '<div style="font-size:0.78rem; color:var(--text-muted); margin-top:3px;">' +
                t('Tercios:') + ' ' +
                tipo.tercios +
                ' &middot; ' + t('Mitad:') + ' ' +
                tipo.mitad +
                '</div>' +
                '</div>' +
                '<div style="text-align:right;">' +
                '<div style="font-size:0.7rem; text-transform:uppercase; color:var(--text-muted); letter-spacing:0.5px; margin-bottom:4px;">' + t('Pleno') + '</div>' +
                '<div style="font-size:1.2rem; font-weight:700; color:var(--accent-green,#4caf50);">x' +
                mults.pleno +
                '</div>' +
                '</div>' +
                '</div>' +
                // Fila 2: chips de multiplicadores
                '<div style="margin-top:8px;">' +
                chipsHtml +
                '</div>' +
                specialHtml +
                restrictHtml +
                // Separador
                '<div style="margin-top:10px; padding-top:10px; border-top:1px solid var(--border-color,rgba(255,255,255,0.08));">' +
                // Fila 3: frecuencia de premios (referencia sin ponderación)
                '<div style="display:flex; gap:20px; flex-wrap:wrap; margin-bottom:6px;">' +
                '<div>' +
                '<span style="font-size:0.7rem; text-transform:uppercase; color:var(--text-muted); letter-spacing:0.5px;">' + t('Ciclo pleno') + '</span>' +
                '<span style="display:block; font-size:0.9rem; font-weight:600; color:var(--text-secondary);">' + t('~1 cada') + ' ' +
                freqPleno +
                ' ' + t('tiradas') + '</span>' +
                '</div>' +
                '<div>' +
                '<span style="font-size:0.7rem; text-transform:uppercase; color:var(--text-muted); letter-spacing:0.5px;">' + t('Ciclo mitad') + '</span>' +
                '<span style="display:block; font-size:0.9rem; font-weight:600; color:var(--text-secondary);">' + t('~1 cada') + ' ' +
                freqMitad +
                ' ' + t('tiradas') + '</span>' +
                '</div>' +
                '<div>' +
                '<span style="font-size:0.7rem; text-transform:uppercase; color:var(--text-muted); letter-spacing:0.5px;">' + t('Ciclo bloque') + '</span>' +
                '<span style="display:block; font-size:0.9rem; font-weight:600; color:var(--text-secondary);">' + t('~1 cada') + ' ' +
                freqBloque +
                ' ' + t('tiradas') + '</span>' +
                '</div>' +
                '</div>' +
                // Fila 4: volatilidad base
                '<div style="font-size:0.75rem; color:var(--text-muted);">' +
                t('Volatilidad base:') + ' <span style="color:' +
                volColor +
                '; font-weight:600;">' +
                volLabel +
                '</span>' +
                ' &mdash; <span style="font-style:italic;">' + t('referencia sin ponderación; el RTP real lo determina el algoritmo') + '</span>' +
                '</div>' +
                '</div>';
        }

        // Mostrar/ocultar filas de apuestas segun disponibilidad del tipo.
        // Si una fila no tiene rowId en el HTML, su toggle no se filtra.
        // Mapa explicito = doc viva de "qué se filtra por qué flag".
        const rowsAFiltrar = [
            { rowId: 'betEspejosRow', checkboxId: 'betEspejos', flag: avail.hasMirrors },
            { rowId: 'betGemelosRow', checkboxId: 'betGemelos', flag: avail.hasTwins },
            { rowId: 'betTrioRow', checkboxId: 'betTrio', flag: avail.hasTrio },
            { rowId: 'betSextetoRow', checkboxId: 'betSexteto', flag: avail.hasSexteto },
            { rowId: 'betCuartetoRow', checkboxId: 'betCuarteto', flag: avail.hasCuarteto },
            { rowId: 'betBloquesRow', checkboxId: 'betBloques', flag: avail.hasBloques },
            { rowId: 'betTerciosRow', checkboxId: 'betTercios', flag: avail.hasTercios },
            { rowId: 'betColumnasRow', checkboxId: 'betColumnas', flag: avail.hasColumnas },
            { rowId: 'betParidadRow', checkboxId: 'betParidad', flag: avail.hasParidad },
            { rowId: 'betMitadRow', checkboxId: 'betMitad', flag: avail.hasMitad },
        ];

        for (const r of rowsAFiltrar) {
            const row = document.getElementById(r.rowId);
            if (row) row.style.display = r.flag ? '' : 'none';
            // Si la apuesta no aplica al tipo, desmarcar el checkbox para que
            // no se guarde en apuestasHabilitadas (defensa contra config sucia
            // al cambiar de tipo: si tenia "bloques" marcado y cambias a
            // rueda10, se desmarca solo).
            if (!r.flag) {
                const checkbox = document.getElementById(r.checkboxId);
                if (checkbox) checkbox.checked = false;
            }
        }

        // Actualizar textos dinamicos de apuestas
        this.updateBetDescriptions(tipo);

        // Actualizar multiplicadores dinamicos
        this.updateMultipliers(tipoId);

        // Refrescar UI de "Presentacion del Cero" (visibilidad inputs + preview)
        this.refreshZeroDisplayUI();
    },

    // Actualiza los spans de multiplicadores segun el tipo de rueda
    updateMultipliers(tipoId) {
        const mults = BET_MULTIPLIERS.forType(tipoId);
        if (!mults) return;

        // Solo actualizamos los que cambian por tipo (no color, paridad, mitad, tercio, columna)
        const updates = {
            multDuo: mults.duo,
            multTrio: mults.trio,
            multCuarteto: mults.cuarteto,
            multSexteto: mults.sexteto,
            // betBloquesMult ya se actualiza en updateBetDescriptions()
        };

        for (const [id, value] of Object.entries(updates)) {
            const el = document.getElementById(id);
            if (el) el.textContent = `x${value}`;
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BRANDING — upload/reset de logos por sorteo (endpoints separados).
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Llena los previews de branding con los assets del config actual
     * @param {Object} assets — config.assets del sorteo (puede ser undefined)
     */
    // Paths de logos default (servidos desde backend/data/uploads/branding/)
    _DEFAULT_LOGOS: {
        logoLoteria: '/data/uploads/branding/default_logoLoteria.svg',
        logoBeneficencia: '/data/uploads/branding/default_logoBeneficencia.png',
        logoSorteo: '/data/uploads/branding/default_logoSorteo.png',
    },

    populateBrandingPreviews(assets, sorteoConfig) {
        const slots = ['logoLoteria', 'logoBeneficencia', 'logoSorteo', 'iconUrl', 'thumbnailUrl'];
        for (let i = 0; i < slots.length; i++) {
            const slot = slots[i];
            const capitalizedSlot = slot.charAt(0).toUpperCase() + slot.slice(1);
            const imgEl = document.getElementById(`brandingPreview${capitalizedSlot}`);
            const labelEl = document.getElementById(`brandingLabel${capitalizedSlot}`);
            if (!imgEl) continue;

            // Slots de integradores: leer de columna directa del sorteo
            const columnSlots = { iconUrl: 'icon_url', thumbnailUrl: 'thumbnail_url' };
            const customUrl = columnSlots[slot]
                ? (sorteoConfig && sorteoConfig[columnSlots[slot]])
                : (assets && assets[slot]);
            const defaultUrl = this._DEFAULT_LOGOS[slot];
            const displayUrl = customUrl || defaultUrl;

            if (displayUrl) {
                imgEl.src = absolutizarAssetUrl(displayUrl);
                imgEl.style.display = 'block';
                if (labelEl) labelEl.style.display = 'none';
            } else {
                imgEl.style.display = 'none';
                if (labelEl) {
                    labelEl.textContent = t('Sin imagen');
                    labelEl.style.display = 'block';
                }
            }
        }
    },

    uploadBrandingAsset(slot) {
        const capitalizedSlot = slot.charAt(0).toUpperCase() + slot.slice(1);
        const fileInput = document.getElementById(`brandingFile${capitalizedSlot}`);
        if (fileInput) fileInput.click();
    },

    async onBrandingFileSelected(slot) {
        const capitalizedSlot = slot.charAt(0).toUpperCase() + slot.slice(1);
        const fileInput = document.getElementById(`brandingFile${capitalizedSlot}`);
        if (!fileInput || !fileInput.files.length) return;

        const file = fileInput.files[0];
        if (file.size > 2 * 1024 * 1024) {
            App.toast(t('Archivo demasiado grande (max 2MB)'), 'error');
            fileInput.value = '';
            return;
        }

        const sorteoConfigId = this._editingSorteoConfigId;
        if (!sorteoConfigId) {
            App.toast(t('Error: no se encontró el ID del sorteo'), 'error');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const result = await Api.uploadFile(`/admin/branding/sorteo/${sorteoConfigId}/upload?slot=${slot}`, formData);
        if (result && result.ok) {
            App.toast(t('Logo actualizado'), 'success');

            // Sincronizar referencia en memoria
            if (this.currentSorteoConfig) {
                const columnSlots = { iconUrl: 'icon_url', thumbnailUrl: 'thumbnail_url' };
                if (columnSlots[slot]) {
                    // Slots de integradores: columna directa del sorteo
                    this.currentSorteoConfig[columnSlots[slot]] = result.url;
                } else {
                    // Slots de branding: config.assets
                    if (!this.currentSorteoConfig.config) this.currentSorteoConfig.config = {};
                    if (!this.currentSorteoConfig.config.assets) this.currentSorteoConfig.config.assets = {};
                    this.currentSorteoConfig.config.assets[slot] = result.url;
                }
            }

            // Actualizar preview
            const imgEl = document.getElementById(`brandingPreview${capitalizedSlot}`);
            const labelEl = document.getElementById(`brandingLabel${capitalizedSlot}`);
            if (imgEl) {
                imgEl.src = absolutizarAssetUrl(result.url) + '?t=' + Date.now();
                imgEl.style.display = 'block';
            }
            if (labelEl) {
                labelEl.textContent = t('Personalizado');
                labelEl.style.color = 'var(--accent-green, #4caf50)';
            }
        }
        fileInput.value = '';
    },

    async resetBrandingAsset(slot) {
        const sorteoConfigId = this._editingSorteoConfigId;
        if (!sorteoConfigId) return;

        const capitalizedSlot = slot.charAt(0).toUpperCase() + slot.slice(1);
        const result = await Api.callAdmin(`/admin/branding/sorteo/${sorteoConfigId}/${slot}`, 'DELETE');
        if (result && result.ok) {
            App.toast(t('Logo restaurado a default'), 'success');

            // Sincronizar referencia en memoria
            if (this.currentSorteoConfig) {
                const columnSlots = { iconUrl: 'icon_url', thumbnailUrl: 'thumbnail_url' };
                if (columnSlots[slot]) {
                    this.currentSorteoConfig[columnSlots[slot]] = null;
                } else if (this.currentSorteoConfig.config && this.currentSorteoConfig.config.assets) {
                    delete this.currentSorteoConfig.config.assets[slot];
                }
            }

            const imgEl = document.getElementById(`brandingPreview${capitalizedSlot}`);
            const labelEl = document.getElementById(`brandingLabel${capitalizedSlot}`);
            if (imgEl) {
                imgEl.src = '';
                imgEl.style.display = 'none';
            }
            if (labelEl) {
                labelEl.textContent = t('Default');
                labelEl.style.color = 'var(--text-muted)';
            }
        }
    },

    updateBetDescriptions(tipo) {
        // Tercios - descripcion con rangos
        const terciosDesc = document.getElementById('betTerciosDesc');
        if (terciosDesc) {
            terciosDesc.textContent = tipo.tercios;
        }

        // Mitad - nombre con rangos
        const mitadName = document.getElementById('betMitadName');
        if (mitadName) {
            mitadName.textContent = `${t('Mitad:')} (${tipo.mitad})`;
        }

        // Bloques - nombre y multiplicador
        const bloquesName = document.getElementById('betBloquesName');
        if (bloquesName) {
            bloquesName.textContent = `${t('Bloque')} (${tipo.blocks})`;
        }

        const bloquesMult = document.getElementById('betBloquesMult');
        if (bloquesMult) {
            bloquesMult.textContent = `x${tipo.blockMult}`;
        }

    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRESENTACIÓN DEL CERO — modo + labels custom + preview canvas fiel.
    //
    // HISTORIA (versiones previas, dejadas como referencia):
    //   v1 (2026-04-17): 3 inputs planos, 1 por cero, con texto simple.
    //       Ejemplo: ['LOTO', 'LOTO+', 'LOTO++']
    //       Limitacion: no reflejaba la estructura de 2 lineas del wheel real.
    //   v2 (acordeon + inputs en fila): mismo formato string-por-cero, mejor UX.
    //       Mismo limite conceptual: no respetaba que el sector tiene label chico
    //       arriba (fsLbl) y numero grande abajo (fsNum).
    //
    // v3 (actual): 2 inputs apilados por cero (top + bot). Formato persistido:
    //   [{top:'LOTO', bot:'37'}, {top:'LOTO+', bot:'38'}, ...]
    //   Backward-compat: si llega el formato viejo (array strings), el backend
    //   lo acepta y el wheel lo interpreta como {top:string, bot:displayValue}.
    //
    // MEDICION RADIAL (size=800px baseline):
    //   fsLbl = 14.4px   fsNum = 36px
    //   Ancho tangencial en linea abajo por tipo:
    //     european/american/triple_zero  ≈ 60px   → max 2 chars a fsNum
    //     mini18                         ≈ 118px  → max 4 chars
    //     mini                           ≈ 173px  → max 7 chars
    //   Ancho tangencial en linea arriba:
    //     european                       ≈ 65px   → max 6 chars a fsLbl
    //     mini18                         ≈ 126px  → max 13 chars
    //     mini                           ≈ 184px  → max 19 chars
    //   Para UX, aplicamos limites conservadores (ver _maxTopForType / _maxBotForType).
    // ====================================================================

    /**
     * Cantidad de sectores "fuera de grilla" (esCero) del tipo de rueda.
     * Derivado de la receta — agregar/sacar sectores fuera de grilla en
     * `recetas-agnosticas.js` mueve este valor sin tocar el admin.
     */
    _zeroCountForType(tipoDeRueda) {
        const cfg = TIPOS_DE_RUEDA_SSOT[tipoDeRueda];
        if (!cfg) {
            throw new Error(`[modal-apariencia] tipo de rueda "${tipoDeRueda}" no existe en las recetas.`);
        }
        const sectores = Array.isArray(cfg.sectoresUniformesDeLaRueda) ? cfg.sectoresUniformesDeLaRueda : [];
        let count = 0;
        for (let i = 0; i < sectores.length; i++) {
            if (sectores[i].esCero === true) count++;
        }
        return count;
    },

    // Cap de longitud de los labels del cero. Ya NO es una "medicion radial"
    // por tipo: el wheel auto-encoge el texto al ancho del sector via maxWidth
    // en fillText (ver wheel-of-fortune.js, _drawWheelNumberText), asi que el
    // texto nunca se desborda sea cual sea su largo. Este cap es solo sanidad
    // (defense-in-depth contra TEXT enorme) y coincide con el limite del
    // backend ZERO_DISPLAY_LABEL_MAX_LEN — SSOT, un solo numero.
    _ZERO_LABEL_MAX_LEN: 12,

    /**
     * Maxlength de la linea de arriba (label chico).
     */
    _maxTopForType(_tipoId) {
        return this._ZERO_LABEL_MAX_LEN;
    },

    /**
     * Maxlength de la linea de abajo (numero grande).
     */
    _maxBotForType(_tipoId) {
        return this._ZERO_LABEL_MAX_LEN;
    },

    /**
     * Default displayValue numerico del sector fuera de grilla (modo POR_NUMERO).
     * Derivado de `sector.bot` de cada sector esCero en la receta — refleja
     * lo que la rueda real pinta. Si el admin no edita los labels, el form y
     * la rueda muestran exactamente lo mismo.
     */
    _defaultDisplayValuesForType(tipoDeRueda) {
        const cfg = TIPOS_DE_RUEDA_SSOT[tipoDeRueda];
        if (!cfg) {
            throw new Error(`[modal-apariencia] tipo de rueda "${tipoDeRueda}" no existe en las recetas.`);
        }
        const sectores = Array.isArray(cfg.sectoresUniformesDeLaRueda) ? cfg.sectoresUniformesDeLaRueda : [];
        const bots = [];
        for (let i = 0; i < sectores.length; i++) {
            const s = sectores[i];
            if (s.esCero === true) bots.push(String(s.bot));
        }
        return bots;
    },

    _defaultTopLabels() {
        return ['LOTO', 'LOTO+', 'LOTO++'];
    },

    /**
     * Carga los valores persistidos en los inputs. Acepta 2 formatos:
     *   - Legacy strings: ['LOTO','LOTO+','LOTO++']
     *   - Nuevo objetos:  [{top:'LOTO', bot:'37'}, ...]
     */
    populateZeroDisplayConfig(modo, labels) {
        const modoFinal = modo || 'POR_NUMERO';
        const radios = document.querySelectorAll('input[name="lotowheelConfigZeroDisplayMode"]');
        for (let i = 0; i < radios.length; i++) {
            radios[i].checked = radios[i].value === modoFinal;
        }
        const safeLabels = Array.isArray(labels) ? labels : [];
        const defTops = this._defaultTopLabels();
        const defBots = this._defaultDisplayValuesForType(
            document.getElementById('lotowheelConfigTipoRuleta')?.value || TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN
        );

        for (let i = 1; i <= 3; i++) {
            const topEl = document.getElementById('lotowheelConfigZeroTop' + i);
            const botEl = document.getElementById('lotowheelConfigZeroBot' + i);
            const raw = safeLabels[i - 1];

            let topVal, botVal;
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
                // El operador configuró este cero explícitamente: respetar sus
                // valores TAL CUAL, incluso vacíos. Un campo vacío es una
                // elección válida (el operador no quiere texto en esa línea) —
                // NO rellenar con el default, sino el campo "rebota" al default
                // cada vez que se reabre el modal y parece que no se guardó.
                topVal = raw.top != null ? String(raw.top).trim() : '';
                botVal = raw.bot != null ? String(raw.bot).trim() : '';
            } else if (typeof raw === 'string' && raw.trim() !== '') {
                // Legacy: string simple → top; bot queda en default numerico
                topVal = raw.trim();
                botVal = defBots[i - 1] || '';
            } else {
                // Sin objeto para este cero → no fue configurado: usar defaults.
                topVal = defTops[i - 1];
                botVal = defBots[i - 1] || '';
            }
            if (topEl) topEl.value = topVal;
            if (botEl) botEl.value = botVal;
        }
        this.refreshZeroDisplayUI();
    },

    onZeroDisplayModeChange() {
        this.refreshZeroDisplayUI();
    },

    onZeroDisplayLabelInput() {
        this.updateZeroDisplayPreview();
    },

    /**
     * Ajusta visibilidad de slots, maxlength por tipo, y dispara preview.
     */
    refreshZeroDisplayUI() {
        const tipoSelect = document.getElementById('lotowheelConfigTipoRuleta');
        if (!tipoSelect) return;
        const tipoId = tipoSelect.value;
        const count = this._zeroCountForType(tipoId);
        const maxTop = this._maxTopForType(tipoId);
        const maxBot = this._maxBotForType(tipoId);

        // Mostrar/ocultar bloque completo segun modo
        const modoActivo = document.querySelector('input[name="lotowheelConfigZeroDisplayMode"]:checked');
        const modoVal = modoActivo ? modoActivo.value : 'POR_NUMERO';
        const textosGroup = document.getElementById('lotowheelConfigZeroTextosGroup');
        if (textosGroup) textosGroup.style.display = modoVal === 'POR_TEXTO' ? '' : 'none';

        // Visibilidad individual + maxlength dinamico
        for (let i = 1; i <= 3; i++) {
            const slot = document.getElementById('lotowheelConfigZeroSlot' + i);
            if (slot) slot.style.display = i <= count ? '' : 'none';

            const topEl = document.getElementById('lotowheelConfigZeroTop' + i);
            const botEl = document.getElementById('lotowheelConfigZeroBot' + i);
            if (topEl) {
                topEl.maxLength = maxTop;
                // Truncar si el valor actual excede el nuevo limite (cambio de tipo)
                if (topEl.value.length > maxTop) topEl.value = topEl.value.slice(0, maxTop);
            }
            if (botEl) {
                botEl.maxLength = maxBot;
                if (botEl.value.length > maxBot) botEl.value = botEl.value.slice(0, maxBot);
            }
        }

        this.updateZeroDisplayPreview();
    },

    /**
     * Actualiza el preview canvas (fiel al wheel) + el badge del summary.
     */
    updateZeroDisplayPreview() {
        const tipoSelect = document.getElementById('lotowheelConfigTipoRuleta');
        if (!tipoSelect) return;
        const tipoId = tipoSelect.value;
        const count = this._zeroCountForType(tipoId);
        const maxTop = this._maxTopForType(tipoId);
        const maxBot = this._maxBotForType(tipoId);
        const modoEl = document.querySelector('input[name="lotowheelConfigZeroDisplayMode"]:checked');
        const modo = modoEl ? modoEl.value : 'POR_NUMERO';

        // Resolver pares {top, bot} segun modo
        const pairs = [];
        const defTops = this._defaultTopLabels();
        const defBots = this._defaultDisplayValuesForType(tipoId);

        if (modo === 'POR_CEROS') {
            for (let i = 0; i < count; i++) pairs.push({ top: null, bot: '0'.repeat(i + 1) });
        } else if (modo === 'POR_TEXTO') {
            for (let i = 1; i <= count; i++) {
                const topVal = document.getElementById('lotowheelConfigZeroTop' + i)?.value?.trim() || '';
                const botVal = document.getElementById('lotowheelConfigZeroBot' + i)?.value?.trim() || '';
                pairs.push({
                    top: topVal || defTops[i - 1],
                    bot: botVal || defBots[i - 1] || '',
                });
            }
        } else {
            // POR_NUMERO: top null, bot = displayValue default
            for (let i = 0; i < count; i++) pairs.push({ top: null, bot: defBots[i] || '' });
        }

        // Badge del summary (texto compacto)
        const badge = document.getElementById('lotowheelConfigZeroAccordionBadge');
        if (badge) {
            const parts = pairs.map(p => {
                if (p.top && p.bot) return `${p.top}/${p.bot}`;
                return p.top || p.bot || '—';
            });
            badge.textContent = '[ ' + parts.join(', ') + ' ]';
        }

        // Fallback texto (cuando el canvas no está disponible)
        const fallback = document.getElementById('lotowheelConfigZeroPreviewTextFallback');
        if (fallback) {
            const parts = pairs.map(p => (p.top && p.bot ? `${p.top}\u2009${p.bot}` : p.top || p.bot || '—'));
            fallback.textContent = '[ ' + parts.join('  ') + ' ]';
            fallback.style.display = pairs.length === 0 ? '' : 'none';
        }

        // Contadores y canvas fiel por cero
        for (let i = 1; i <= 3; i++) {
            const counter = document.getElementById('lotowheelConfigZeroCounter' + i);
            const canvas = document.getElementById('lotowheelConfigZeroCanvas' + i);
            if (i > count) {
                if (canvas) canvas.style.display = 'none';
                continue;
            }
            const topEl = document.getElementById('lotowheelConfigZeroTop' + i);
            const botEl = document.getElementById('lotowheelConfigZeroBot' + i);
            const topLen = topEl ? topEl.value.length : 0;
            const botLen = botEl ? botEl.value.length : 0;
            if (counter) {
                const topWarn = topLen > maxTop * 0.8;
                const botWarn = botLen > maxBot * 0.8;
                const colorTop = topLen >= maxTop ? '#f87171' : topWarn ? '#fbbf24' : 'var(--text-tertiary)';
                const colorBot = botLen >= maxBot ? '#f87171' : botWarn ? '#fbbf24' : 'var(--text-tertiary)';
                counter.innerHTML = `<span style="color:${colorTop}">${topLen}/${maxTop}</span>  ·  <span style="color:${colorBot}">${botLen}/${maxBot}</span>`;
            }
            if (canvas) {
                canvas.style.display = '';
                this._drawZeroPreviewCanvas(canvas, pairs[i - 1], tipoId);
            }
        }
    },

    /**
     * Renderea en un canvas el sector zero FIEL al wheel real, usando
     * drawSectorPreview (shared/components/zero-sector-preview.js) con
     * color verde LOTO canonico.
     *
     * Este helper dibuja:
     *   - Sector curvado con arco real del tipo de rueda (european 9.7° vs triple 9.2° vs mini 27.7°).
     *   - Texto rotado tangente al arco (no horizontal).
     *   - Gradiente verde metálico LOTO.
     *   - Pines dorados en los bordes del sector.
     *   - Reparto vertical fsLbl/fsNum identico al wheel de produccion.
     *
     * HISTORIA: v1 de este preview dibujaba un rectangulo verde con texto plano,
     * no reflejaba la curvatura ni el arco real. Reemplazado 2026-04-17 por el
     * renderer profesional compartido.
     */
    _drawZeroPreviewCanvas(canvas, pair, tipoId) {
        drawSectorPreview(canvas, {
            tipoRuleta: tipoId,
            top: pair?.top || '',
            bot: pair?.bot || '',
            color: COLOR_VERDE_LOTO_CANONICO,
        });
    },

    collectZeroDisplayMode() {
        const el = document.querySelector('input[name="lotowheelConfigZeroDisplayMode"]:checked');
        return el ? el.value : 'POR_NUMERO';
    },

    /**
     * Devuelve los labels a persistir como array de {top,bot}.
     *   - Si modo != POR_TEXTO → null (no ensuciar la DB ni invalidar cache).
     *   - Si modo == POR_TEXTO → array cortado al count del tipo actual.
     */
    collectZeroDisplayLabels() {
        const modo = this.collectZeroDisplayMode();
        if (modo !== 'POR_TEXTO') return null;

        const tipoSelect = document.getElementById('lotowheelConfigTipoRuleta');
        const tipoId = tipoSelect ? tipoSelect.value : TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN;
        const count = this._zeroCountForType(tipoId);

        const labels = [];
        let algunoNoVacio = false;
        for (let i = 1; i <= count; i++) {
            const topEl = document.getElementById('lotowheelConfigZeroTop' + i);
            const botEl = document.getElementById('lotowheelConfigZeroBot' + i);
            const top = topEl ? topEl.value.trim() : '';
            const bot = botEl ? botEl.value.trim() : '';
            if (top || bot) algunoNoVacio = true;
            labels.push({ top, bot });
        }
        return algunoNoVacio ? labels : null;
    },
});
